import { Chip, Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { BADGE, FONT_SIZE } from "../../../../theme/tokens";
import type { PbjCategoryScore, PbjModelUpsertBody, PbjPrediction } from "../../../../utils/models";
import { brandWash } from "../colors";

// The three turnover measures, in the order CMS reports them, with the override each one writes.
// Total and RN are percentages; Administrator departures is a count.
const TURNOVER_MEASURES = [
	{ categoryId: "4", field: "manualTotalTurnoverPct", isCount: false },
	{ categoryId: "5", field: "manualRnTurnoverPct", isCount: false },
	{ categoryId: "6", field: "manualAdminTurnoverCount", isCount: true },
] as const;

type Props = {
	prediction: PbjPrediction;
	draft: PbjModelUpsertBody;
	onDraftChange: (next: PbjModelUpsertBody) => void;
};

/** What the field shows: the admin's own entry if there is one, otherwise the scored value. */
const displayedValue = (draftValue: number | null | undefined, category: PbjCategoryScore | undefined): string => {
	if (draftValue !== null && draftValue !== undefined) return String(draftValue);
	return category ? String(category.rawValue) : "";
};

/**
 * Where THIS measure's number came from, said under its own field. Each measure resolves on its own
 * (manual → CASPER → blank in CASPER → InHouse estimate), so one line for all three would misdescribe
 * any mixture — and "blank in CASPER" is the case a user most needs told, because that measure is
 * dropped from the rating entirely rather than estimated.
 */
const measureSourceKey = (draftValue: number | null | undefined, category: PbjCategoryScore | undefined, hasCcn: boolean): string => {
	if (draftValue !== null && draftValue !== undefined) return "pbj_model.setup.step.turnover.source.yours";
	if (category?.source === "casper") return "pbj_model.setup.step.turnover.source.casper";
	if (category?.source === "casper_missing") return "pbj_model.setup.step.turnover.source.casper_missing";
	// No CASPER row read yet: with a CCN just picked, one is coming at Finish; without one, never.
	return hasCcn ? "pbj_model.setup.step.turnover.source.pending" : "pbj_model.setup.step.turnover.source.internal";
};

/**
 * Setup step 2 — the three turnover measures. With a CCN they arrive pre-filled from the facility's
 * last submitted CASPER quarter and the step is a confirmation; without one there is nothing to
 * pre-fill and the same three fields are where the numbers are typed. Either way an entry here
 * becomes the manual override, which outranks every other source.
 */
export const SetupTurnoverStep: FC<Props> = ({ prediction, draft, onDraftChange }) => {
	const { t } = useTranslation();
	const theme = useTheme();

	// The three measures resolve INDEPENDENTLY — one can come from CASPER while the next is blank
	// there and the third carries a hand-typed override. So the source is named per field, and the
	// line at the top only sets the scene. A single global verdict read as "all three are CASPER"
	// over a mixture, which is the one thing a user cannot check from the screen.
	const intro = draft.ccn ? "pbj_model.setup.step.turnover.intro_linked" : "pbj_model.setup.step.turnover.intro_manual";

	const handleChange = (field: (typeof TURNOVER_MEASURES)[number]["field"]) => (event: React.ChangeEvent<HTMLInputElement>) => {
		const raw = event.target.value.trim();
		// A cleared field drops the override and hands the measure back to CASPER / the internal estimate.
		const parsed = raw === "" ? null : Number(raw);
		if (parsed !== null && !Number.isFinite(parsed)) return;
		onDraftChange({ ...draft, [field]: parsed });
	};

	return (
		<Stack spacing={2}>
			<Typography variant="body2" color="text.secondary">
				{t(intro)}
			</Typography>
			{TURNOVER_MEASURES.map((measure) => {
				const category = prediction.categories.find((candidate) => candidate.id === measure.categoryId);
				const sourceKey = measureSourceKey(draft[measure.field], category, Boolean(draft.ccn));
				return (
					<Stack key={measure.categoryId} spacing={0.5}>
						<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
							<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
								{category ? category.title : t(`pbj_model.setup.step.turnover.measure.${measure.categoryId}`)}
							</Typography>
							{category?.source === "casper" && (
								<Chip
									label={t("pbj_model.scoring.casper.badge")}
									size="small"
									sx={{
										height: BADGE.TAG_CHIP_HEIGHT_SM,
										fontSize: FONT_SIZE.LABEL_XXS,
										bgcolor: brandWash(theme),
										color: theme.palette.primary.main,
										fontWeight: 700,
									}}
									data-testid={`pbj-model-setup-turnover-casper-${measure.categoryId}`}
								/>
							)}
						</Stack>
						<SystemValidationTextField
							fullWidth
							inputType="number"
							label={measure.isCount ? t("pbj_model.scoring.manual.input_count") : t("pbj_model.scoring.manual.input_pct")}
							value={displayedValue(draft[measure.field], category)}
							onChange={handleChange(measure.field)}
							reserveHeightForHelperText={false}
							inputProps={{
								inputProps: measure.isCount
									? { min: 0, step: 1, "data-testid": `pbj-model-setup-turnover-input-${measure.categoryId}` }
									: { min: 0, max: 100, step: "any", "data-testid": `pbj-model-setup-turnover-input-${measure.categoryId}` },
							}}
							defaultHelperText={t(sourceKey)}
						/>
					</Stack>
				);
			})}
		</Stack>
	);
};
